/**
 * Putting a bubble straight into a world - for tests, tools and debug scenes, not the rules. A placed
 * bubble floats (or is a shot) with the timer its kind would have; one holding a monster takes the
 * monster into it as a shot would.
 */

import { FIRE, FULL, JUST_BLOWN, LETTER, LIGHTNING, TRAPPED, WATER } from './codes';
import { addBubble, freeSlot } from './slots';
import { BUBBLE_LIFE, HOLE_BUBBLE_LIFE, SHOT_STEPS, TRAP_TIME } from './tuning';
import type { Bubble, BubbleContent, Facing, PlayerId, World } from '../types';

export interface PlacedBubble {
  /** VIC-frame X, Y (as the players'). */
  x: number;
  y: number;
  content?: BubbleContent;
  owner?: PlayerId | -1;
  /** 'shot': just blown, the full 8 steps to go, flying `dir`. Default 'float'. */
  phase?: 'shot' | 'float';
  dir?: Facing;
  /** Life / escape timer; default by kind (125 blown, 130 from a hole, 160 trapped). */
  timer?: number;
}

/**
 * A bubble in the highest free slot, or null with none free. A floating bubble's row is set so the
 * C64's row and Y agree; a shot's row is taken from Y as a blow takes it.
 */
export function placeBubble(world: World, spec: PlacedBubble): Bubble | null {
  const slot = freeSlot(world);
  const dy = (spec.y - 0x15) & 0xff;
  if (slot < 0) return null;
  const content = spec.content ?? { kind: 'empty' };
  const shot = spec.phase === 'shot';
  const sub = dy & 6;
  const row = shot || sub === 0 ? dy >> 3 : (dy >> 3) + 1;
  const code = shot ? JUST_BLOWN : codeOf(content);
  const timer = spec.timer ?? (code === TRAPPED ? TRAP_TIME : spec.owner === -1 ? HOLE_BUBBLE_LIFE : BUBBLE_LIFE);
  const owner = spec.owner ?? 0;
  const b = addBubble(world, slot, {
    x: spec.x, y: spec.y, owner, content,
    motion: { code, row, subY: sub, steps: shot ? SHOT_STEPS : 0, timer, dir: spec.dir ?? 1, credit: owner },
  });
  if (content.kind === 'monster') {
    const mon = world.monsters.find((m) => m.id === content.monsterId);
    if (mon) {
      mon.state = 'trapped';
      mon.bubbleId = b.id;
      mon.x = b.x;
      mon.y = b.y;
    }
  }
  return b;
}

function codeOf(content: BubbleContent): number {
  switch (content.kind) {
    case 'monster': return TRAPPED;
    case 'water': return WATER;
    case 'fire': return FIRE;
    case 'lightning': return LIGHTNING;
    case 'letter': return LETTER + 2 * Math.max(0, Math.min(4, content.letter));
    default: return FULL;
  }
}
